import { useTranslation } from 'react-i18next';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AssistantDirectionOutlinedIcon from '@mui/icons-material/AssistantDirectionOutlined';
import { useState, ReactElement } from 'react';
import { DriveOnDisplayableTaskDto as DriveonTask } from '@/generated/model';
import { TaskDetailDialog } from '@/components/logbook/task/TaskDetailDialog';
import { useDriveOnContext } from '@/contexts/DriveOnContext';
import { MapAppPicker } from '@/components/common/MapAppPicker';
import { getTaskAddress } from '@/components/common/TaskHelper';

import './TaskChooser.css';

type TaskChooserProps = {
  task: DriveonTask;
  setTask: (task: DriveonTask) => void;
  enableSelect: boolean;
  expanded: boolean | undefined;
  logbook: boolean;
};

export function TaskChooser({ task, setTask, enableSelect, expanded, logbook }: TaskChooserProps) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<ReactElement | undefined>(undefined);
  const driveonCtx = useDriveOnContext();
  const [gpsDialog, setGpsDialog] = useState<ReactElement | undefined>(undefined);

  function checkIfEmpty(label: string | undefined | 0): string {
    if (label) {
      return label;
    }
    return t('menetlevel.taskchooser.nodata');
  }

  function confirm(task: DriveonTask) {
    function onClose() {
      setDialog(undefined);
    }

    setDialog(<TaskDetailDialog onClose={onClose} task={task} checkIfEmpty={checkIfEmpty} />);
  }

  function showSelectButton() {
    if (enableSelect) {
      return (
        <Button
          onClick={() => setTask(task)}
          variant="outlined"
          startIcon={<PlayArrowOutlinedIcon />}
          sx={{ 
            margin: '2px',
            borderColor: 'rgba(0, 0, 0)',
            color: 'white', 
            backgroundColor: 'red !important' }}
        >
          {t('menetlevel.taskchooser.button.start')}
        </Button>
      );
    }
  }

  function setExpandIcon() {
    if (!expanded) {
      return <ExpandMoreIcon />;
    }
  }

  function showGpsDialog(task: DriveonTask) {
    function onClose() {
      setGpsDialog(undefined);
    }

    setGpsDialog(<MapAppPicker type={'gps'} task={task} onClose={onClose} />);
  }

  function showLocation(task: DriveonTask) {
    return `${t('menetlevel.displaytask.eventuslocation')}: ${getTaskAddress(task)}`;
  }

  function isTaskSelected() : boolean {
    return driveonCtx.task?.eventusId === task.eventusId && !logbook
  }

  function showSelectedAccordion() {
    return (
      <>
        <Accordion 
          disableGutters={true} 
          expanded={false}
          elevation={0}
          sx={{ 
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
            border: '3px solid rgb(15, 90, 38)',
            '&:first-of-type': {
              borderRadius: '8px 8px 0 0',
            },
            '&:last-of-type': {
              borderRadius: '0 0 8px 8px',
            },
          }} 
        >
          <AccordionSummary 
            sx={{
              backgroundColor: 'rgb(161, 194, 171)',
            }}
          >
            <Typography component="span">
              <div>
                <b>{showLocation(task)}</b>
              </div>
              <div>
                {t('menetlevel.taskdetails.eventusid')}: {checkIfEmpty(task.eventusId)}
              </div>
              <div>
                {t('menetlevel.displaytask.ordernumber')}: {checkIfEmpty(task.costCenter)}
              </div>
            </Typography>
          </AccordionSummary>
        </Accordion>
      </>
      );
    }

  function showAccordion() {
    return (
      <>
      <Accordion 
        disableGutters={true} 
        expanded={expanded}
        elevation={0}
        sx={{ 
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
          border: '2px solid rgb(44, 39, 39)',
          '&:first-of-type': {
            borderRadius: '8px 8px 0 0',
          },
          '&:last-of-type': {
            borderRadius: '0 0 8px 8px',
          },
        }} 
      >
        <AccordionSummary 
          expandIcon={setExpandIcon()}
          sx={{
            backgroundColor: 'rgb(199, 199, 199)',
          }}
        >
          <Typography component="span">
            <div>
              <b>{showLocation(task)}</b>
            </div>
            <div>
              {t('menetlevel.taskdetails.eventusid')}: {checkIfEmpty(task.eventusId)}
            </div>
            <div>
              {t('menetlevel.displaytask.ordernumber')}: {checkIfEmpty(task.costCenter)}
            </div>
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: 'rgb(156, 153, 153)',
          }}
        >
          <ButtonGroup fullWidth variant="contained">
            <Button
              onClick={() => {
                confirm(task);
              }}
              variant="outlined"
              startIcon={<InfoOutlinedIcon />}
              sx={{ 
                margin: '2px',
                borderColor: 'rgba(0, 0, 0)',
                color: 'white', 
                backgroundColor: 'darkgrey !important',
              }}
            >
              {t('menetlevel.taskchooser.button.details')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssistantDirectionOutlinedIcon />}
              sx={{ 
                margin: '2px',
                borderColor: 'rgba(0, 0, 0)',
                color: 'white', 
                backgroundColor: 'grey !important'
              }}
              onClick={() => showGpsDialog(task)}
            >
              {t('menetlevel.taskchooser.button.navigation')}
            </Button>
            {showSelectButton()}
          </ButtonGroup>
        </AccordionDetails>
        {dialog}
        {gpsDialog}
      </Accordion>
      </>
    );
  }

  return isTaskSelected() ?  showSelectedAccordion() : showAccordion();
}
